import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { Logout, ArrowDropDown, Person, Dashboard } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearUserDetails } from '../../../redux-store/slices/userSlice';
import './Header.css';
import OcuaLogo from '../../../assets/images/ocua-logo.png';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { httpRequest } from 'src/services/httpRequest';
import { logout } from 'src/redux-store/slices/logoutSlice';

interface RootState {
  user: {
    userDetails: {
      userName?: string;
      FirstName?: string;
      LastName?: string;
      EmailID?: string;
      UserRole?: string;
      accessToken?: string;
      refreshToken?: string;
      [key: string]: any;
    };
  };
}

function Header() {
  const { state: locationState, township, uuid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();
  const isLoginPage =
    location.pathname === `/${locationState}/${township}/${uuid}/login`;

  const isRegister =
    location.pathname === `/${locationState}/${township}/${uuid}/register`;

  const tenantBasePath = `/${locationState}/${township}/${uuid}`;

  const authPaths = [
    `${tenantBasePath}/login`,
    `${tenantBasePath}/forgot-password`,
    `${tenantBasePath}/register`,
    tenantBasePath // base path without extra
  ];

  const isAuthPage = authPaths.some((path) => location.pathname === path);

  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const displayName =
    userDetails.FirstName && userDetails.LastName
      ? `${userDetails.FirstName} ${userDetails.LastName}`
      : userDetails.userName || userDetails.EmailID || '';

  const getInitials = () => {
    if (userDetails.FirstName && userDetails.LastName) {
      return `${userDetails.FirstName.charAt(0)}${userDetails.LastName.charAt(
        0
      )}`;
    } else if (userDetails.EmailID) {
      return userDetails.EmailID.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const userRole = userDetails.UserRole || '';

  const handleLoginClick = () => {
    navigate(`/${locationState}/${township}/${uuid}/login`);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const openLogoutConfirm = () => {
    handleUserMenuClose();
    setLogoutConfirmOpen(true);
  };

  const closeLogoutConfirm = () => {
    setLogoutConfirmOpen(false);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      const resultResponse = await httpRequest('POST', '/auth/logout', {}, {});
      if (resultResponse.status === 'success') {
        dispatch(clearUserDetails());

        closeLogoutConfirm();
        handleUserMenuClose();

        // Clear redirectPath so old URL is not used
        sessionStorage.removeItem('redirectPath');

        navigate(`/${locationState}/${township}/${uuid}/login`, {
          replace: true
        });
        dispatch(logout());
      }
    } catch (err) {
      console.log('Error:', err);
      toast.error(err.message || 'Failed to logout');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleNavigateToProfile = () => {
    handleUserMenuClose();
    if (
      userDetails?.UserRole === 'Admin' ||
      userDetails?.UserRole === 'General Admin'
    ) {
      navigate(
        `/${locationState}/${township}/${uuid}/bid-portal/Admin/my-profile`
      );
    } else {
      navigate(`/${locationState}/${township}/${uuid}/bid-portal/profile`);
    }
  };

  const handleNavigateToDashboard = () => {
    handleUserMenuClose();
    if (
      userDetails?.UserRole === 'Admin' ||
      userDetails?.UserRole === 'General Admin'
    ) {
      navigate(`/${locationState}/${township}/${uuid}/bid-portal/Admin/home`);
    } else {
      navigate(`/${locationState}/${township}/${uuid}/bid-portal/home`);
    }
  };

  return (
    <>
      <AppBar position="static" className="header-container">
        <Toolbar className="header-toolbar">
          <Box
            className="header-left"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Box
              component="img"
              src={OcuaLogo}
              alt="Company Logo"
              className="header-logo"
              sx={{
                width: { xs: '50px', sm: '80px', md: '100px' },
                height: { xs: '50px', sm: '80px', md: '100px' },
                marginLeft: !isAuthPage ? '36px' : '12px'
              }}
            />
            <Box
              display="flex"
              flexDirection="column"
              gap={{ xs: 0.5, sm: 1 }}
              sx={{
                maxWidth: { xs: '200px', sm: '100%' }
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#1919F8',
                  letterSpacing: { xs: '0.5px', sm: '1px' },
                  fontSize: { xs: '16px', sm: '18px', md: '25px' },
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: 1.1,
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                OCEAN COUNTY UTILITIES AUTHORITY
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  m: 0,
                  fontSize: { xs: '9px', sm: '10px', md: '11px' },
                  color: '#666',
                  letterSpacing: '0.5px',
                  fontWeight: 'bold',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                HELPING TO PRESERVE THE WATER RESOURCES OF OCEAN AND SOUTHERN
                MONMOUTH COUNTIES...
              </Typography>
            </Box>
          </Box>
          {userDetails.userName && !isAuthPage ? (
            <div className="user-dropdown-container">
              <Tooltip title="Account settings">
                <Button
                  onClick={handleUserMenuOpen}
                  className="user-menu-button"
                  aria-expanded={Boolean(anchorEl)}
                  sx={{
                    borderRadius: '8px',
                    padding: '6px 12px',
                    transition: 'background-color 0.3s',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <Avatar
                    sx={{
                      width: { xs: 28, sm: 32 },
                      height: { xs: 28, sm: 32 },
                      bgcolor: 'primary.dark',
                      fontSize: { xs: '12px', sm: '14px' },
                      fontWeight: 'bold'
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box
                    sx={{
                      ml: 1,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      display: { sm: 'flex' }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {displayName}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {userRole}
                    </Typography>
                  </Box>
                  <ArrowDropDown className="dropdown-arrow" sx={{ ml: 0.5 }} />
                </Button>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                    mt: 1.5,
                    minWidth: 200,
                    borderRadius: '8px',
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0
                    }
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {displayName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontSize: '12px' }}
                  >
                    {userDetails.EmailID}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem onClick={handleNavigateToProfile} sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  My Profile
                </MenuItem>
                <MenuItem onClick={handleNavigateToDashboard} sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    <Dashboard fontSize="small" />
                  </ListItemIcon>
                  Dashboard
                </MenuItem>
                <MenuItem
                  onClick={openLogoutConfirm}
                  sx={{ py: 1.5, color: 'error.main' }}
                >
                  <ListItemIcon>
                    <Logout fontSize="small" color="error" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </div>
          ) : !isLoginPage && !isRegister ? (
            // Home page (not logged in) → show BOTH buttons
            <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
              <Button
                variant="contained"
                onClick={handleLoginClick}
                sx={{
                  fontSize: { xs: '12px', sm: '14px' }
                }}
              >
                Login
              </Button>

              <Button
                variant="contained"
                onClick={() =>
                  navigate(`/${locationState}/${township}/${uuid}/register`)
                }
                sx={{
                  fontSize: { xs: '12px', sm: '14px' }
                }}
              >
                Register
              </Button>
            </Box>
          ) : !isRegister ? (
            // Login page → show Register
            <Button
              variant="contained"
              onClick={() =>
                navigate(`/${locationState}/${township}/${uuid}/register`)
              }
            >
              Register
            </Button>
          ) : isRegister && (
            // Register page → show Login
            <Button variant="contained" onClick={handleLoginClick}>
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutConfirmOpen}
        onClose={isLoggingOut ? undefined : closeLogoutConfirm}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            pb: 1
          }}
        >
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 2
            }}
          >
            <Avatar
              sx={{
                width: 60,
                height: 60,
                bgcolor: 'error.light',
                mb: 2
              }}
            >
              <Logout fontSize="large" />
            </Avatar>
            <Typography variant="body1" sx={{ textAlign: 'center', mb: 1 }}>
              Are you sure you want to log out?
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center' }}
            >
              You will need to log in again to access your account.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            padding: '16px 24px 24px'
          }}
        >
          <Button
            onClick={closeLogoutConfirm}
            variant="outlined"
            disabled={isLoggingOut}
            sx={{
              minWidth: '120px',
              borderRadius: '8px'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            variant="contained"
            color="error"
            disabled={isLoggingOut}
            sx={{
              minWidth: '120px',
              borderRadius: '8px',
              ml: 2
            }}
            startIcon={
              isLoggingOut ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Logout />
              )
            }
          >
            {isLoggingOut ? 'Logging out...' : 'Logout'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Header;
